import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Hook for managing NIIMBOT B1 BLE printer connection and printing.
 * Uses @mmote/niimbluelib for Web Bluetooth communication.
 * Falls back to browser print when BLE is unavailable.
 */

const LABEL_WIDTH = 400;   // B1 printhead width in pixels (50mm @ 203dpi)
const LABEL_HEIGHT = 240;  // 30mm label height @ 203dpi

export const usePrinter = () => {
    const [connected, setConnected] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [deviceName, setDeviceName] = useState(null);
    const [bleSupported, setBleSupported] = useState(false);
    const clientRef = useRef(null);

    useEffect(() => {
        setBleSupported(!!navigator.bluetooth);
    }, []);

    const connect = useCallback(async () => {
        if (!navigator.bluetooth) {
            return { success: false, error: 'Web Bluetooth not supported in this browser' };
        }

        setConnecting(true);
        try {
            const { NiimbotBluetoothClient } = await import('@mmote/niimbluelib');
            const client = new NiimbotBluetoothClient();

            client.on('connect', (info) => {
                setConnected(true);
                setDeviceName(info?.deviceName || 'NIIMBOT B1');
            });

            client.on('disconnect', () => {
                setConnected(false);
                setDeviceName(null);
                clientRef.current = null;
            });

            const result = await client.connect();
            clientRef.current = client;

            if (result.result === 'connected') {
                return { success: true, deviceName: result.deviceName };
            }
            return { success: false, error: 'Connection failed' };
        } catch (err) {
            setConnected(false);
            const msg = err?.message || String(err);
            if (msg.includes('cancelled') || msg.includes('canceled')) {
                return { success: false, error: 'Pairing cancelled' };
            }
            return { success: false, error: msg };
        } finally {
            setConnecting(false);
        }
    }, []);

    const disconnect = useCallback(async () => {
        try {
            if (clientRef.current?.isConnected()) {
                await clientRef.current.disconnect();
            }
        } catch {}
        clientRef.current = null;
        setConnected(false);
        setDeviceName(null);
    }, []);

    const renderLabelCanvas = useCallback((printData) => {
        const canvas = document.createElement('canvas');
        canvas.width = LABEL_WIDTH;
        canvas.height = LABEL_HEIGHT;
        const ctx = canvas.getContext('2d');

        // White background
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, LABEL_WIDTH, LABEL_HEIGHT);
        ctx.fillStyle = '#000000';

        // Safe print margins (B1 has ~3-4mm unprintable edges)
        const ML = 30;  // left margin
        const MR = 30;  // right margin
        const MT = 20;  // top margin
        const PW = LABEL_WIDTH - ML - MR; // printable width ~340px
        let y = MT;

        // Business name
        if (printData.business_name) {
            ctx.font = 'bold 12px Arial, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText(printData.business_name.toUpperCase(), LABEL_WIDTH / 2, y + 10, PW);
            y += 15;
            ctx.fillRect(ML, y, PW, 1);
            y += 4;
        }

        // Item name (dominant)
        ctx.font = '900 24px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(printData.item_name.toUpperCase(), LABEL_WIDTH / 2, y + 20, PW);
        y += 25;
        ctx.fillRect(ML, y, PW, 1);
        y += 4;

        // Opened date
        ctx.font = 'bold 18px Arial, sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText('OPENED:', ML, y + 17);
        ctx.font = '900 20px Arial, sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText(printData.opened_at, LABEL_WIDTH - MR, y + 17);
        y += 24;

        // Use By (inverted bar)
        ctx.fillStyle = '#000000';
        ctx.fillRect(ML - 4, y, PW + 8, 28);
        ctx.fillStyle = '#FFFFFF';
        ctx.font = 'bold 18px Arial, sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText('USE BY:', ML + 4, y + 20);
        ctx.font = '900 20px Arial, sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText(printData.use_by, LABEL_WIDTH - MR - 4, y + 20);
        y += 32;
        ctx.fillStyle = '#000000';

        // Allergens
        if (printData.allergens?.length > 0) {
            ctx.font = 'bold 18px Arial, sans-serif';
            ctx.textAlign = 'left';
            ctx.fillText('ALLERGENS: ' + printData.allergens.join(', '), ML, y + 17, PW);
            y += 22;
        }

        // Storage instructions
        if (printData.storage_instructions) {
            ctx.font = 'bold 17px Arial, sans-serif';
            ctx.textAlign = 'left';
            ctx.fillText(printData.storage_instructions, ML, y + 16, PW);
            y += 20;
        }

        // Printed by (bottom right, within margins)
        ctx.font = 'bold 15px Arial, sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText('By: ' + printData.printed_by, LABEL_WIDTH - MR, LABEL_HEIGHT - 8);

        return canvas;
    }, []);

    const printViaBle = useCallback(async (printData) => {
        if (!clientRef.current?.isConnected()) {
            return { success: false, error: 'Printer not connected' };
        }

        try {
            const { ImageEncoder } = await import('@mmote/niimbluelib');

            const canvas = renderLabelCanvas(printData);
            const encoded = ImageEncoder.encodeCanvas(canvas, 'none');

            const printTask = clientRef.current.abstraction.newPrintTask('B1', {
                totalPages: 1,
                statusPollIntervalMs: 100,
                statusTimeoutMs: 10000,
                pageTimeoutMs: 10000,
            });

            await printTask.printInit();
            await printTask.printPage(encoded, 1);
            await printTask.waitForFinished();
            await clientRef.current.abstraction.printEnd();

            return { success: true };
        } catch (err) {
            return { success: false, error: err?.message || 'Print failed' };
        }
    }, [renderLabelCanvas]);

    return {
        connected,
        connecting,
        deviceName,
        bleSupported,
        connect,
        disconnect,
        printViaBle,
        renderLabelCanvas,
    };
};
